import React, { useState, useEffect, useMemo } from 'react'
import {
  TruckIcon,
  HomeIcon,
  TrashIcon,
  CalendarDaysIcon
} from '@heroicons/react/24/outline'
import { Popup } from 'semantic-ui-react'
import { useGetPickupAddressDisplayQuery } from '../../../redux/apis'
import { formatCurrency } from '../../../utils/formatters'
import { useHistory } from 'react-router-dom'

// Custom CSS for 500px breakpoint - add at the top of the file
const styles = `
  @media (min-width: 500px) {
    .desktop-grid {
      display: grid;
      grid-template-columns: repeat(12, minmax(0, 1fr));
      gap: 1rem;
      align-items: start;
    }
    .desktop-contents {
      display: contents;
    }
    .desktop-flex {
      display: flex;
    }
    .desktop-hidden {
      display: none;
    }
    .desktop-block {
      display: block;
    }
    .desktop-col-span-3 {
      grid-column: span 3 / span 3;
    }
    .desktop-col-span-6 {
      grid-column: span 6 / span 6;
    }
    .desktop-col-last {
      grid-column-start: 10;
      grid-column-end: 13;
    }
    .desktop-flex-col {
      flex-direction: column;
    }
    .desktop-items-start {
      align-items: flex-start;
    }
    .desktop-items-end {
      align-items: flex-end;
    }
    .desktop-justify-start {
      justify-content: flex-start;
    }
    .desktop-space-y-1 > * + * {
      margin-top: 0.25rem;
    }
    .desktop-space-y-3 > * + * {
      margin-top: 1rem;
    }
    .desktop-text-left {
      text-align: left;
    }
    .desktop-mb-0 {
      margin-bottom: 0;
    }
    .desktop-space-x-2 > * + * {
      margin-left: 0.5rem;
    }
    .desktop-w-28 {
      width: 7rem;
    }
    .desktop-h-28 {
      height: 7rem;
    }
    
    /* Security deposit display for tablet/medium screens (>500px but <1024px) */
    @media (max-width: 1023px) {
      .security-deposit {
        display: flex;
        flex-direction: column;
        align-items: flex-end;
      }
      .security-deposit-label {
        font-size: 0.75rem;
        color: #4B5563;
      }
      .security-deposit-amount {
        font-weight: 500;
      }
    }
  }
`

const CartProduct = ({
  product,
  handleDelete,
  handleQuantityChange,
  isOutOfStock = false,
  currency
}) => {
  const [pickupLocation, setPickupLocation] = useState(null)
  const history = useHistory()
  // Function to navigate to product detail page
  const navigateToProduct = (e) => {
    // Prevent event from triggering parent element handlers
    e.stopPropagation()
    history.push(`/product/${product.id}`)
  }

  const itemType = product.type

  const getAddressId = () => {
    if (!product) return null
    if (typeof product.pickupAddress === 'number') return product.pickupAddress
    if (product.pickupAddress?.id) return product.pickupAddress.id
    if (product.product?.pickupAddress) {
      if (typeof product.product.pickupAddress === 'number')
        return product.product.pickupAddress
      if (product.product.pickupAddress.id)
        return product.product.pickupAddress.id
    }
    return null
  }
  const addressId = getAddressId()

  const { 
    data: addressData, 
    isLoading: addressLoading,
    isError: addressError
  } = useGetPickupAddressDisplayQuery(addressId, { skip: !addressId })

  const getDepositAmount = () => {
    if (!product || itemType !== 'rent') return 0
    if (product.rentDeposit && Number(product.rentDeposit) > 0)
      return Number(product.rentDeposit)
    if (product.deposit && Number(product.deposit) > 0)
      return Number(product.deposit)
    return 0
  }
  const depositAmount = getDepositAmount()

  useEffect(() => {
    if (addressLoading) return

    if (addressData) {
      const city = addressData.city || ''
      const postalCode = addressData.postalCode || addressData.zipCode || ''
      if (city && postalCode) {
        setPickupLocation(`${city}, ${postalCode}`)
        return
      }
      if (city) {
        setPickupLocation(city)
        return
      }
      if (postalCode) {
        setPickupLocation(postalCode)
        return
      }
    }
    if (product.pickupAddress?.city) {
      const pa = product.pickupAddress
      const city = pa.city || ''
      const postalCode = pa.postalCode || pa.zipCode || ''
      if (city && postalCode) {
        setPickupLocation(`${city}, ${postalCode}`)
        return
      }
      if (city) {
        setPickupLocation(city)
        return
      }
      if (postalCode) {
        setPickupLocation(postalCode)
        return
      }
    }
    if (
      addressId &&
      product.deliveryMethod === 'pickup' &&
      !addressData &&
      !addressError
    )
      setPickupLocation('Loading location...')
    else if (addressId && product.deliveryMethod === 'pickup' && addressError)
      setPickupLocation('Location error')
  }, [product, addressData, addressLoading, addressError, addressId])

  let displayPrice = 0
  if (itemType === 'rent') {
    displayPrice = product.rentalPeriod?.price || 0
    if (
      displayPrice === 0 &&
      product.rentPriceAndTime?.length > 0 &&
      product.rentalPeriod?.option &&
      product.rentalPeriod?.number
    ) {
      const selectedOptionDetails = product.rentPriceAndTime.find(
        opt =>
          opt.option === product.rentalPeriod.option &&
          opt.number === product.rentalPeriod.number
      )
      displayPrice = selectedOptionDetails?.price || product.price || 0
    } else if (displayPrice === 0) {
      displayPrice = product.price || 0
    }
    } else {
    displayPrice = product.price || 0
  }

  if (product.productOptions && product.productOptions.length > 0) {
    product.productOptions.forEach(option => {
      if (option.priceModifier) {
        displayPrice += parseFloat(option.priceModifier)
      }
    })
  }

  const productCurrency = currency || product.currency || 'USD'

  const rentalPeriodText = useMemo(() => {
    if (itemType !== 'rent' || !product.rentalPeriod) return null
    const number = product.rentalPeriod.number || 1
    const option = product.rentalPeriod.option || 'day'
    return `${number} ${option}${number > 1 ? 's' : ''}`
  }, [product.rentalPeriod, itemType])

  const imageUrl =
    product.photos?.[0]?.formats?.small?.url || product.photos?.[0]?.url

  const renderDeliveryMethod = (isMobile = false) =>
    product.deliveryMethod && (
      <div
        className={`flex items-center text-xs ${
          isMobile
            ? 'text-gray-600 mt-1'
            : 'sm:text-sm text-gray-600 font-normal'
        }`}
      >
        {product.deliveryMethod === 'shipping' ? (
          <>
            <TruckIcon
              className={`h-4 w-4 mr-1.5 ${
                isMobile ? 'text-gray-500' : 'text-gray-500'
              }`}
            />
                  <span>
                    Shipping
              {product.estimatedDelivery
                ? ` (${product.estimatedDelivery})`
                : ''}
                  </span>
                </>
              ) : product.deliveryMethod === 'pickup' ? (
                <>
            <HomeIcon
              className={`h-4 w-4 mr-1.5 ${
                isMobile ? 'text-gray-500' : 'text-gray-500'
              }`}
            />
                  <span>
                    Pickup
              {pickupLocation
                ? ` (${pickupLocation})`
                : addressLoading && addressId && !addressError
                ? ' (loading...)'
                : addressId && addressError
                ? ' (location error)'
                : ''}
                  </span>
                </>
              ) : null}
            </div>
    )

  const renderRentalPeriod = (isMobile = false) =>
    itemType === 'rent' &&
    rentalPeriodText && (
      <div
        className={`flex items-center ${
          isMobile
            ? 'text-xs text-gray-600 mt-1'
            : 'text-xs sm:text-sm text-gray-600 font-normal'
        }`}
      >
        <CalendarDaysIcon
          className={`h-4 w-4 mr-1.5 ${
            isMobile ? 'text-gray-500' : 'text-gray-500 flex-shrink-0'
          }`}
        />
        <span>{rentalPeriodText}</span>
      </div>
    )

  const renderTypeBadge = (isMobile = false) => (
    <div className={`flex items-center ${isMobile ? 'mb-1' : 'space-x-2'}`}>
      {itemType === 'rent' && (
        <span className='px-2.5 py-1 text-xs font-bold text-blue-800 bg-blue-100 rounded-full uppercase tracking-wide'>
          Rental
        </span>
      )}
      {itemType === 'buy' && (
        <span className='px-2.5 py-1 text-xs font-bold text-green-800 bg-green-100 rounded-full uppercase tracking-wide'>
          Buy
        </span>
          )}
        </div>
  )

  const renderQuantityControls = (isMobile = false) => (
    <div
      className={`flex items-center ${
        isMobile ? 'space-x-2' : 'space-x-1 sm:space-x-1.5'
      } ${isMobile ? 'mt-1' : ''}`}
    >
      {!isOutOfStock ? (
        <div className='flex items-center space-x-1 bg-gray-100 rounded-full p-0.5'>
          <Popup
            trigger={(
            <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleQuantityChange(product, 'decrease');
                }}
                className={`p-1 text-gray-600 hover:text-gray-900 hover:bg-gray-200 rounded-full ${
                  isMobile ? '' : 'sm:p-1.5'
                }`}
                aria-label='Decrease quantity'
            >
              <svg
                  className={`h-4 w-4 ${isMobile ? '' : 'sm:h-4 sm:w-4'}`}
                fill='none'
                stroke='currentColor'
                viewBox='0 0 24 24'
              >
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth='2'
                  d='M20 12H4'
                />
              </svg>
            </button>
            )}
            content='Remove item from cart'
            position='top center'
            size='mini'
            disabled={product.quantity > 1}
            style={{ zIndex: 9999 }}
          />

          <span
            className={`text-sm font-medium text-gray-800 w-5 text-center ${
              isMobile ? '' : 'sm:w-6'
            }`}
          >
            {product.quantity}
          </span>

          <Popup
            trigger={(
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleQuantityChange(product, 'increase');
                }}
                className={`p-1 text-gray-600 hover:text-gray-900 hover:bg-gray-200 rounded-full disabled:opacity-50 disabled:cursor-not-allowed ${
                  isMobile ? '' : 'sm:p-1.5'
                }`}
                
                aria-label='Increase quantity'
              >
                <svg
                  className={`h-4 w-4 ${isMobile ? '' : 'sm:h-4 sm:w-4'}`}
                  fill='none'
                  stroke='currentColor'
                  viewBox='0 0 24 24'
                >
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth='2'
                    d='M12 6v6m0 0v6m0-6h6m-6 0H6'
                  />
                </svg>
              </button>
            )}
            content='No more available in stock'
            position='top center'
            size='mini'
            disabled={
              product.quantity < product.stock
            }
            style={{ zIndex: 9999 }}
          />
        </div>
      ) : (
        <span
          className={`text-sm font-medium text-gray-700 ${
            isMobile ? '' : 'text-xs sm:text-sm'
          }`}
        >
          Qty: {product.quantity}
        </span>
      )}
    </div>
  )

  const renderSecurityDeposit = (isMobile = false) =>
    itemType === 'rent' &&
    depositAmount > 0 &&
    (isMobile ? (
      <div className={`flex items-center text-xs text-gray-600 mt-0.5`}>
        <span>
          Security Deposit +{formatCurrency(depositAmount, productCurrency)}
        </span>
      </div>
    ) : (
      <div className='security-deposit text-xs sm:text-sm text-gray-600 font-normal'>
        <span className='security-deposit-label'>Security Deposit </span>

        <span className='security-deposit-amount'>
          +{formatCurrency(depositAmount, productCurrency)}
        </span>
      </div>
    ))

  return (
    <>
      {/* Include custom CSS */}
      <style>{styles}</style>

      <div
        className={`bg-white shadow-md rounded-lg p-4 sm:p-6 mb-6 relative ${
          isOutOfStock ? 'opacity-60' : ''
        }`}
      >
        {isOutOfStock && (
          <div className='absolute inset-0 bg-white/90 flex flex-col items-center justify-center z-10 rounded-lg p-4'>
            <p className='text-gray-700 font-semibold mb-3 text-center text-base sm:text-lg'>
              Sorry, this item is out of stock
            </p>
            <button
              onClick={() => handleDelete(product)}
              className='text-sm sm:text-base text-red-600 hover:text-red-800 font-medium py-2 px-4 bg-red-100 hover:bg-red-200 rounded-lg flex items-center shadow-sm'
              aria-label='Remove out of stock item'
            >
              <TrashIcon className='h-4 w-4 sm:h-5 sm:w-5 mr-1.5' /> Remove
            </button>
          </div>
        )}
        {/* Main container: flex-col for mobile, grid for desktop (500px+) */}
        <div className='flex flex-col desktop-grid'>
          {/* --- MOBILE ROW 1 / DESKTOP COLS 1 & 2 --- */}
          <div className='flex flex-row items-start space-x-3 sm:space-x-4 mb-3 desktop-hidden'>
            {/* Delete Button + Image */}
            <div className='flex items-start space-x-1 flex-shrink-0'>
              <button
                onClick={() => handleDelete(product)}
                className='mt-0.5 p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-100 rounded-full transition-colors duration-150'
                aria-label='Remove item'
                title='Remove item'
              >
                <TrashIcon className='h-5 w-5' />
              </button>
              <img
                src={imageUrl}
                alt={product.name}
                className='w-20 h-20 sm:w-24 sm:h-24 object-cover rounded-lg shadow cursor-pointer'
                onClick={navigateToProduct}
              />
          </div>

            {/* Product Details (Name, Options) */}
            <div className='flex-1 min-w-0 text-right'>
              <h4 
                className='text-base sm:text-lg font-semibold text-gray-800 leading-tight cursor-pointer hover:text-brand-teal'
                onClick={navigateToProduct}
              >
                {product.name}
              </h4>
              <div className='text-xs sm:text-sm text-gray-600 space-y-0.5 sm:space-y-1'>
                {product.productOptions && product.productOptions.length > 0 ? (
                  product.productOptions.map((option, idx) => (
                    <p key={idx}>
                      <span className='font-medium text-gray-700'>
                        {option.customOptionName || option.optionType}:
                      </span>{' '}
                      {option.optionValue}
                      {option.priceModifier > 0 &&
                        ` (+${formatCurrency(
                          option.priceModifier,
                          productCurrency
                        )})`}
                      {option.priceModifier < 0 &&
                        ` (-${formatCurrency(
                          Math.abs(option.priceModifier),
                          productCurrency
                        )})`}
                    </p>
                  ))
                ) : (
                  <>
                    {product.color && (
                      <p>
                        <span className='font-medium text-gray-700'>
                          Color:
                        </span>{' '}
                        {Array.isArray(product.colors)
                          ? product.colors[0]?.name
                          : product.color}
                      </p>
                    )}
                    {product.size && (
                      <p>
                        <span className='font-medium text-gray-700'>Size:</span>{' '}
                        {Array.isArray(product.sizes)
                          ? product.sizes[0]?.name
                          : product.size}
                      </p>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>

          {/* --- DESKTOP VERSION COLUMNS (4 distinct columns) --- */}
          {/* Column 1: Desktop Image + Delete */}
          <div className='hidden desktop-block desktop-col-span-3'>
            <div className='flex items-start space-x-2'>
              <button
                onClick={() => handleDelete(product)}
                className='mt-0.5 p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-100 rounded-full transition-colors duration-150'
                aria-label='Remove item'
                title='Remove item'
              >
                <TrashIcon className='h-5 w-5' />
              </button>
              <img
                src={imageUrl}
                alt={product.name}
                className='desktop-w-28 desktop-h-28 object-cover rounded-lg shadow cursor-pointer'
                onClick={navigateToProduct}
              />
            </div>
          </div>

          {/* Column 2: Desktop Product Details */}
          <div className='hidden desktop-block desktop-col-span-3 desktop-space-y-1'>
            <h4 
              className='text-base sm:text-lg font-semibold text-gray-800 leading-tight cursor-pointer hover:text-brand-teal'
              onClick={navigateToProduct}
            >
              {product.name}
            </h4>
            <div className='text-xs sm:text-sm text-gray-600 space-y-0.5 sm:space-y-1'>
              {product.productOptions && product.productOptions.length > 0 ? (
                product.productOptions.map((option, idx) => (
                  <p key={idx}>
                    <span className='font-medium text-gray-700'>
                      {option.customOptionName || option.optionType}:
                    </span>{' '}
                    {option.optionValue}
                    {option.priceModifier > 0 &&
                      ` (+${formatCurrency(
                        option.priceModifier,
                        productCurrency
                      )})`}
                    {option.priceModifier < 0 &&
                      ` (-${formatCurrency(
                        Math.abs(option.priceModifier),
                        productCurrency
                      )})`}
                  </p>
                ))
              ) : (
                <>
                  {product.color && (
                    <p>
                      <span className='font-medium text-gray-700'>Color:</span>{' '}
                      {Array.isArray(product.colors)
                        ? product.colors[0]?.name
                        : product.color}
                    </p>
                  )}
                  {product.size && (
                    <p>
                      <span className='font-medium text-gray-700'>Size:</span>{' '}
                      {Array.isArray(product.sizes)
                        ? product.sizes[0]?.name
                        : product.size}
                    </p>
                  )}
                </>
              )}
            </div>
            <p className='text-xs sm:text-sm italic text-gray-500'>
              {product.customNote}
            </p>
          </div>

          {/* Column 3: Desktop Type Badge, Rental Period, Delivery */}
          <div className='hidden desktop-block desktop-col-span-3 desktop-space-y-3'>
            {renderTypeBadge(false)}
            {renderRentalPeriod(false)}
            {renderDeliveryMethod(false)}
          </div>

          {/* Column 4: Desktop Price, Security Deposit, Quantity (positioned at the far right) */}
          <div className='hidden desktop-block desktop-col-last desktop-space-y-1'>
            <div className='flex flex-col items-end space-y-1'>
              <div className='text-lg sm:text-xl font-bold text-gray-900'>
                {formatCurrency(displayPrice, productCurrency)}
              </div>
              {renderSecurityDeposit(false)}
              {renderQuantityControls(false)}
            </div>
          </div>

          {/* --- MOBILE ROWS (visible only on mobile, hidden on 500px+) --- */}
          {/* UNIFIED MOBILE ROW 2: (Type/Rental/Delivery/Note Left) | (Price/Deposit/Qty Right) */}
          <div className='mt-3 pt-3 border-t border-gray-100 flex flex-row justify-between items-start w-full desktop-hidden'>
            {/* Left Stack */}
            <div className='flex flex-col items-start space-y-1'>
              {renderTypeBadge(true)}
              {renderRentalPeriod(true)}
              {renderDeliveryMethod(true)}
              {product.customNote && (
                <p className='text-xs italic text-gray-500 mt-1'>
                  Note: {product.customNote}
                  </p>
                )}
            </div>
            {/* Right Stack */}
            <div className='flex flex-col items-end space-y-1'>
              <div className='text-lg font-bold text-gray-900'>
                {formatCurrency(displayPrice, productCurrency)}
              </div>
              {renderSecurityDeposit(true)}
              {renderQuantityControls(true)}
            </div>
          </div>
        </div>{' '}
        {/* End of main grid/flex container */}
      </div>
    </>
  )
}

export default CartProduct
